// ==========================================
// Paper Types
// ==========================================
export const PAPER_TYPE = {
  CREAM: 'Cream Paper',
  WHITE: 'White Paper',
  ART_MATTE: 'Art Matte Paper',
} as const;

export type PaperType = (typeof PAPER_TYPE)[keyof typeof PAPER_TYPE];

// Paper physical properties used for spine width calculation
export const PAPER_PROPERTIES: Record<PaperType, { gsm: number; bulk: number }> = {
  [PAPER_TYPE.CREAM]:     { gsm: 75, bulk: 1.37 },
  [PAPER_TYPE.WHITE]:     { gsm: 75, bulk: 1.43 },
  [PAPER_TYPE.ART_MATTE]: { gsm: 90, bulk: 1.37 },
};

// ==========================================
// Binding Types
// ==========================================
export const BINDING_TYPE = {
  PAPERBACK: 'Paperback',
  HARDBACK: 'Hardback',
} as const;

export type BindingType = (typeof BINDING_TYPE)[keyof typeof BINDING_TYPE];

// Spine offset added per binding type (in mm)
export const BINDING_SPINE_OFFSET: Record<BindingType, number> = {
  [BINDING_TYPE.PAPERBACK]: 0.6,
  [BINDING_TYPE.HARDBACK]:  2,
};

// Minimum page counts per binding type × paper type
export const MIN_PAGES: Record<string, Record<PaperType, number>> = {
  [BINDING_TYPE.PAPERBACK]: {
    [PAPER_TYPE.CREAM]:      32,
    [PAPER_TYPE.WHITE]:      32,
    [PAPER_TYPE.ART_MATTE]:  64,
  },
  [BINDING_TYPE.HARDBACK]: {
    [PAPER_TYPE.CREAM]:      80,
    [PAPER_TYPE.WHITE]:      80,
    [PAPER_TYPE.ART_MATTE]:  80,
  },
};

// ==========================================
// Book Sizes
// ==========================================
export const BOOK_SIZE = {
  '5.5x8.5': '5.5x8.5',
  '8.5x11':  '8.5x11',
  '6x9':     '6x9',
} as const;

export type BookSize = (typeof BOOK_SIZE)[keyof typeof BOOK_SIZE];

export const BOOK_SIZE_OPTIONS = [
  { value: BOOK_SIZE['5.5x8.5'], label: '5.5 x 8.5', description: 'Poetry & Fiction' },
  { value: BOOK_SIZE['8.5x11'],  label: '8.5 x 11',   description: 'Coffee Table & Art Books' },
  { value: BOOK_SIZE['6x9'],     label: '6 x 9',      description: 'Non-Fiction' },
] as const;

// Returns all binding+paper combinations that satisfy pageCount, excluding the current selection.
export const getSuggestedAlternatives = (
  pageCount: number,
  currentBinding: string,
  currentPaper: string,
): Array<{ bindingType: string; paperType: PaperType; minPages: number }> => {
  const suggestions: Array<{ bindingType: string; paperType: PaperType; minPages: number }> = [];
  for (const [binding, papers] of Object.entries(MIN_PAGES)) {
    for (const [paper, min] of Object.entries(papers) as [PaperType, number][]) {
      if (binding === currentBinding && paper === currentPaper) continue;
      if (pageCount >= min) suggestions.push({ bindingType: binding, paperType: paper, minPages: min });
    }
  }
  return suggestions;
};

// Cover dimensions in inches per book size
export const BOOK_COVER_DIMENSIONS: Record<BookSize, { width: number; height: number }> = {
  [BOOK_SIZE['5.5x8.5']]: { width: 5.5, height: 8.5 },
  [BOOK_SIZE['8.5x11']]:  { width: 8.5, height: 11 },
  [BOOK_SIZE['6x9']]:     { width: 6,   height: 9 },
};
